import React, { useState, useEffect, useRef } from 'react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import {
  X,
  Send,
  Trash2,
  Copy,
  Check,
  Sparkles,
  Bot,
  User,
  MessageSquare,
  AlertCircle
} from 'lucide-react';

const STORAGE_KEY = 'moxpal_ai_chat_history_v1';
const WHATSAPP_NUMBER = '919929371541';

const formatCurrentTime = () => {
  const now = new Date();
  let hour = now.getHours();
  const minute = now.getMinutes().toString().padStart(2, '0');
  const period = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${hour}:${minute} ${period}`;
};

// Render formatted text with bold, bullet, and emojis
const FormattedText = ({ text, isUser }) => {
  if (!text) return null;

  // Split by newlines
  const lines = text.split('\n');

  return (
    <div style={{ lineHeight: '1.5', fontSize: '13.5px', wordBreak: 'break-word' }}>
      {lines.map((line, lineIdx) => {
        // Parse bold *text* and bullet •
        const parts = line.split(/(\*[^*]+\*)/g);

        return (
          <div key={lineIdx} style={{ minHeight: line.trim() === '' ? '8px' : 'auto' }}>
            {parts.map((part, pIdx) => {
              if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
                return (
                  <strong
                    key={pIdx}
                    style={{
                      fontWeight: '700',
                      color: isUser ? '#FFFFFF' : '#0F172A'
                    }}
                  >
                    {part.slice(1, -1)}
                  </strong>
                );
              }
              return <span key={pIdx}>{part}</span>;
            })}
          </div>
        );
      })}
    </div>
  );
};

const AiSupportChatModal = ({ isOpen, onClose, userProfile }) => {
  const { t } = useLang();
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const userName = userProfile?.business_name || userProfile?.owner_name || localStorage.getItem('business_name') || 'User';
  const userMobile = userProfile?.mobile || localStorage.getItem('mobile') || '';

  // Initial suggestions
  const defaultSuggestions = [
    t(LangKeys.ai_support_sugg_plan) || '💎 प्लान के क्या फायदे हैं?',
    t(LangKeys.ai_support_sugg_dairy) || '🥛 डेयरी पासबुक कैसे उपयोग करें?',
    t(LangKeys.ai_support_sugg_party) || '👥 नया ग्राहक/खाता कैसे जोड़ें?',
    t(LangKeys.ai_support_sugg_backup) || '💾 डेटा बैकअप कैसे लें?',
    t(LangKeys.ai_support_sugg_bill) || '🧾 नया बिल कैसे बनाएं?'
  ];

  // Load chat history from LocalStorage
  useEffect(() => {
    if (!isOpen) return;

    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setMessages(parsed);
          setSuggestions(defaultSuggestions);
          return;
        }
      }
    } catch (_) {}

    // Welcome greeting
    const welcomeMsg = {
      sender: 'ai',
      text: t(LangKeys.ai_support_welcome_msg) || 'नमस्ते! 🙏 मैं *Moxpal Khata* का स्मार्ट AI सहायक हूँ।\n\nमैं आपके खाता, बिलिंग, डेयरी पासबुक या सब्सक्रिप्शन प्लान से जुड़े सभी सवालों में मदद करने के लिए तैयार हूँ। आप क्या जानना चाहते हैं?',
      time: formatCurrentTime()
    };
    setMessages([welcomeMsg]);
    setSuggestions(defaultSuggestions);
  }, [isOpen]);

  // Auto scroll to bottom
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading, isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  // Save messages to LocalStorage
  const saveHistory = (newMessages) => {
    try {
      const sliced = newMessages.length > 60 ? newMessages.slice(newMessages.length - 60) : newMessages;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sliced));
    } catch (_) {}
  };

  const handleSendMessage = async (queryText) => {
    const text = (queryText || inputText).trim();
    if (!text || isLoading) return;

    const isFirstMessage = messages.length <= 1;

    const newMsg = {
      sender: 'user',
      text,
      time: formatCurrentTime()
    };

    const updated = [...messages, newMsg];
    setMessages(updated);
    setInputText('');
    setIsLoading(true);
    saveHistory(updated);

    // Prepare history payload for multi-turn conversational AI context
    const historyList = updated
      .filter(m => m.sender === 'user' || m.sender === 'ai')
      .map(m => ({
        role: m.sender === 'user' ? 'user' : 'model',
        text: m.text
      }));

    try {
      const response = await fetch('https://khata.moxpal.com/api/support/ai_chat.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json; charset=UTF-8' },
        body: JSON.stringify({
          message: text,
          mobile: userMobile,
          name: userName,
          is_first_in_24h: isFirstMessage,
          history: historyList
        })
      });

      if (response.ok) {
        const data = await response.json();
        const reply = data.reply || t(LangKeys.ai_support_welcome_msg) || 'मैं आपकी क्या मदद कर सकता हूँ?';

        const aiMsg = {
          sender: 'ai',
          text: reply,
          time: formatCurrentTime()
        };

        const finalMsgs = [...updated, aiMsg];
        setMessages(finalMsgs);
        saveHistory(finalMsgs);

        if (Array.isArray(data.suggestions) && data.suggestions.length > 0) {
          setSuggestions(data.suggestions);
        }
      } else {
        const errMsg = {
          sender: 'ai',
          text: t(LangKeys.ai_support_server_error) || 'सर्वर से जुड़ने में समस्या आई। कृपया पुनः प्रयास करें या WhatsApp पर संपर्क करें।',
          time: formatCurrentTime()
        };
        const finalMsgs = [...updated, errMsg];
        setMessages(finalMsgs);
        saveHistory(finalMsgs);
      }
    } catch (e) {
      const netErrMsg = {
        sender: 'ai',
        text: t(LangKeys.ai_support_net_error) || 'इंटरनेट कनेक्शन में समस्या आ रही है। कृपया नेटवर्क चेक करें।',
        time: formatCurrentTime()
      };
      const finalMsgs = [...updated, netErrMsg];
      setMessages(finalMsgs);
      saveHistory(finalMsgs);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleCopy = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  const handleClearHistory = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_) {}

    const welcomeMsg = {
      sender: 'ai',
      text: t(LangKeys.ai_support_welcome_msg) || 'नमस्ते! 🙏 मैं *Moxpal Khata* का स्मार्ट AI सहायक हूँ।\n\nमैं आपके खाता, बिलिंग, डेयरी पासबुक या सब्सक्रिप्शन प्लान से जुड़े सभी सवालों में मदद करने के लिए तैयार हूँ। आप क्या जानना चाहते हैं?',
      time: formatCurrentTime()
    };
    setMessages([welcomeMsg]);
    setSuggestions(defaultSuggestions);
    setShowClearConfirm(false);
  };

  const handleOpenWhatsApp = () => {
    const text = encodeURIComponent('नमस्ते Moxpal Support Team, मुझे Moxpal Khata में सहायता चाहिए।');
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${text}`, '_blank');
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* MODAL HEADER */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.botAvatar}>
              <img
                src="/logo.png"
                alt="AI"
                style={{ width: '24px', height: '24px', objectFit: 'contain' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <span style={styles.onlineDot} />
            </div>
            <div>
              <div style={styles.headerTitle}>
                {t(LangKeys.help_ai_title) || 'Moxpal AI Support'}
              </div>
              <div style={styles.headerStatus}>
                <span style={styles.liveBadge}>{t(LangKeys.help_ai_live_badge) || '24x7 LIVE'}</span>
                <span style={styles.statusText}>{t(LangKeys.ai_support_online_status) || '● 24x7 त्वरित सहायता'}</span>
              </div>
            </div>
          </div>

          <div style={styles.headerActions}>
            <button
              onClick={() => setShowClearConfirm(true)}
              style={styles.iconBtn}
              title={t(LangKeys.ai_support_clear_title) || 'चैट इतिहास साफ़ करें'}
            >
              <Trash2 size={18} color="#94A3B8" />
            </button>
            <button onClick={onClose} style={styles.closeBtn}>
              <X size={20} color="#FFFFFF" />
            </button>
          </div>
        </div>

        {/* MESSAGES LIST CONTAINER */}
        <div style={styles.messageList}>
          {messages.map((msg, i) => {
            const isUser = msg.sender === 'user';

            return (
              <div
                key={i}
                style={{
                  ...styles.messageRow,
                  justifyContent: isUser ? 'flex-end' : 'flex-start'
                }}
              >
                {!isUser && (
                  <div style={styles.botIconWrapper}>
                    <img
                      src="/logo.png"
                      alt="AI"
                      style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                    />
                  </div>
                )}

                <div
                  style={{
                    ...styles.messageBubble,
                    backgroundColor: isUser ? '#1D4ED8' : '#FFFFFF',
                    color: isUser ? '#FFFFFF' : '#1E293B',
                    border: isUser ? 'none' : '1px solid #E2E8F0',
                    borderBottomRightRadius: isUser ? '4px' : '18px',
                    borderBottomLeftRadius: isUser ? '18px' : '4px',
                    boxShadow: isUser
                      ? '0 3px 10px rgba(29, 78, 216, 0.2)'
                      : '0 2px 8px rgba(0, 0, 0, 0.04)'
                  }}
                >
                  <FormattedText text={msg.text} isUser={isUser} />

                  <div
                    style={{
                      ...styles.messageMeta,
                      justifyContent: isUser ? 'flex-end' : 'space-between',
                      color: isUser ? 'rgba(255,255,255,0.7)' : '#94A3B8'
                    }}
                  >
                    {!isUser && (
                      <button
                        onClick={() => handleCopy(msg.text, i)}
                        style={styles.copyBtn}
                        title={t(LangKeys.ai_support_copied) || 'कॉपी करें'}
                      >
                        {copiedIdx === i ? <Check size={12} color="#10B981" /> : <Copy size={12} color="#94A3B8" />}
                        <span style={{ fontSize: '10px' }}>{copiedIdx === i ? 'Copied' : 'Copy'}</span>
                      </button>
                    )}
                    <span style={{ fontSize: '10.5px' }}>{msg.time}</span>
                  </div>
                </div>

                {isUser && (
                  <div style={styles.userIconWrapper}>
                    {userProfile?.logo_url ? (
                      <img
                        src={userProfile.logo_url}
                        alt="User"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                      />
                    ) : (
                      <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#FFFFFF' }}>
                        {userName ? userName[0].toUpperCase() : 'U'}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* TYPING INDICATOR */}
          {isLoading && (
            <div style={{ ...styles.messageRow, justifyContent: 'flex-start' }}>
              <div style={styles.botIconWrapper}>
                <img
                  src="/logo.png"
                  alt="AI"
                  style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                />
              </div>
              <div style={styles.typingBubble}>
                <span style={styles.typingDot} />
                <span style={{ ...styles.typingDot, animationDelay: '0.2s' }} />
                <span style={{ ...styles.typingDot, animationDelay: '0.4s' }} />
                <span style={{ fontSize: '12px', color: '#64748B', marginLeft: '6px' }}>
                  {t(LangKeys.ai_support_typing) || 'AI सहायक उत्तर लिख रहे हैं...'}
                </span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* QUICK SUGGESTIONS CAROUSEL */}
        <div style={styles.suggestionsSection}>
          <div style={styles.suggestionsScroll}>
            {suggestions.map((sugg, idx) => (
              <button
                key={idx}
                onClick={() => handleSendMessage(sugg)}
                disabled={isLoading}
                style={styles.suggestionChip}
              >
                <Sparkles size={12} color="#10B981" />
                <span>{sugg}</span>
              </button>
            ))}
          </div>
        </div>

        {/* FOOTER INPUT BAR */}
        <div style={styles.inputBar}>
          <div style={styles.inputContainer}>
            <input
              ref={inputRef}
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t(LangKeys.ai_support_input_hint) || 'अपना सवाल या समस्या यहाँ पूछें...'}
              disabled={isLoading}
              style={styles.textInput}
            />
            <button
              onClick={() => handleSendMessage()}
              disabled={isLoading || !inputText.trim()}
              style={{
                ...styles.sendBtn,
                backgroundColor: inputText.trim() && !isLoading ? '#1D4ED8' : '#CBD5E1',
                cursor: inputText.trim() && !isLoading ? 'pointer' : 'not-allowed'
              }}
            >
              <Send size={16} color="#FFFFFF" />
            </button>
          </div>

          <div style={styles.humanSupportRow}>
            <span style={{ fontSize: '11px', color: '#64748B' }}>
              संतुष्ट नहीं हैं?
            </span>
            <button onClick={handleOpenWhatsApp} style={styles.humanWhatsAppBtn}>
              <span style={{ fontSize: '12px' }}>💬</span>
              <span>{t(LangKeys.help_whatsapp_title) || 'Contact Us (WhatsApp)'}</span>
            </button>
          </div>
        </div>

        {/* CLEAR CHAT CONFIRM MODAL */}
        {showClearConfirm && (
          <div style={styles.confirmOverlay}>
            <div style={styles.confirmBox}>
              <div style={styles.confirmIcon}>
                <Trash2 size={24} color="#DC2626" />
              </div>
              <h4 style={styles.confirmTitle}>
                {t(LangKeys.ai_support_clear_title) || 'चैट इतिहास साफ़ करें?'}
              </h4>
              <p style={styles.confirmText}>
                {t(LangKeys.ai_support_clear_confirm) || 'क्या आप पुरानी बातचीत को साफ़ करके नई चैट शुरू करना चाहते हैं?'}
              </p>
              <div style={styles.confirmActions}>
                <button
                  onClick={() => setShowClearConfirm(false)}
                  style={styles.cancelBtn}
                >
                  {t(LangKeys.cancel) || 'रद्द करें'}
                </button>
                <button
                  onClick={handleClearHistory}
                  style={styles.deleteConfirmBtn}
                >
                  {t(LangKeys.delete) || 'साफ़ करें'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.7)',
    backdropFilter: 'blur(6px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '16px'
  },
  modal: {
    width: '100%',
    maxWidth: '560px',
    height: '90vh',
    maxHeight: '740px',
    backgroundColor: '#F8FAFC',
    borderRadius: '24px',
    boxShadow: '0 25px 60px rgba(0, 0, 0, 0.35)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative'
  },
  header: {
    background: 'linear-gradient(135deg, #0F172A, #1E293B)',
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  botAvatar: {
    width: '42px',
    height: '42px',
    borderRadius: '50%',
    backgroundColor: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
  },
  onlineDot: {
    position: 'absolute',
    bottom: '1px',
    right: '1px',
    width: '11px',
    height: '11px',
    borderRadius: '50%',
    backgroundColor: '#10B981',
    border: '2px solid #0F172A'
  },
  headerTitle: {
    fontSize: '16px',
    fontWeight: '800',
    color: '#FFFFFF',
    letterSpacing: '-0.01em'
  },
  headerStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '2px'
  },
  liveBadge: {
    backgroundColor: '#059669',
    color: '#FFFFFF',
    fontSize: '9px',
    fontWeight: '800',
    padding: '1px 6px',
    borderRadius: '8px'
  },
  statusText: {
    fontSize: '11px',
    color: '#10B981',
    fontWeight: '600'
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  iconBtn: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    border: 'none',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background 0.15s'
  },
  closeBtn: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    border: 'none',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background 0.15s'
  },
  messageList: {
    flex: 1,
    padding: '16px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px'
  },
  messageRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    width: '100%'
  },
  botIconWrapper: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E8F0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: '2px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)'
  },
  userIconWrapper: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #3B82F6, #1D4ED8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: '2px',
    boxShadow: '0 2px 6px rgba(29, 78, 216, 0.3)'
  },
  messageBubble: {
    maxWidth: '82%',
    padding: '12px 14px',
    borderRadius: '18px',
    position: 'relative',
    lineHeight: '1.45'
  },
  messageMeta: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '8px',
    gap: '10px'
  },
  copyBtn: {
    border: 'none',
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer',
    color: '#94A3B8',
    padding: '2px 4px',
    borderRadius: '4px'
  },
  typingBubble: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: '18px',
    borderBottomLeftRadius: '4px',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
  },
  typingDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: '#10B981',
    display: 'inline-block',
    animation: 'pulse 1.4s infinite ease-in-out both'
  },
  suggestionsSection: {
    padding: '6px 14px',
    borderTop: '1px solid #E2E8F0',
    backgroundColor: '#FFFFFF'
  },
  suggestionsScroll: {
    display: 'flex',
    gap: '8px',
    overflowX: 'auto',
    paddingBottom: '4px',
    scrollbarWidth: 'none'
  },
  suggestionChip: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '20px',
    border: '1px solid #E2E8F0',
    backgroundColor: '#F8FAFC',
    fontSize: '12px',
    fontWeight: '600',
    color: '#334155',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  },
  inputBar: {
    padding: '12px 16px',
    backgroundColor: '#FFFFFF',
    borderTop: '1px solid #E2E8F0',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#F1F5F9',
    borderRadius: '14px',
    padding: '4px 6px 4px 14px',
    border: '1px solid #CBD5E1'
  },
  textInput: {
    flex: 1,
    border: 'none',
    backgroundColor: 'transparent',
    fontSize: '13.5px',
    color: '#0F172A',
    outline: 'none',
    padding: '8px 0',
    fontFamily: 'inherit'
  },
  sendBtn: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.15s ease'
  },
  humanSupportRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    paddingTop: '2px'
  },
  humanWhatsAppBtn: {
    border: 'none',
    backgroundColor: 'transparent',
    color: '#16A34A',
    fontSize: '11.5px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer',
    textDecoration: 'underline'
  },
  confirmOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.65)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    zIndex: 10
  },
  confirmBox: {
    width: '100%',
    maxWidth: '340px',
    backgroundColor: '#FFFFFF',
    borderRadius: '20px',
    padding: '24px 20px',
    textAlign: 'center',
    boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
  },
  confirmIcon: {
    width: '52px',
    height: '52px',
    borderRadius: '50%',
    backgroundColor: '#FEF2F2',
    border: '1px solid #FEE2E2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 14px auto'
  },
  confirmTitle: {
    fontSize: '16px',
    fontWeight: '800',
    color: '#0F172A',
    margin: '0 0 8px 0'
  },
  confirmText: {
    fontSize: '13px',
    color: '#64748B',
    lineHeight: '1.4',
    margin: '0 0 20px 0'
  },
  confirmActions: {
    display: 'flex',
    gap: '10px'
  },
  cancelBtn: {
    flex: 1,
    padding: '10px',
    borderRadius: '10px',
    border: '1px solid #CBD5E1',
    backgroundColor: '#FFFFFF',
    color: '#475569',
    fontWeight: '700',
    fontSize: '13px',
    cursor: 'pointer'
  },
  deleteConfirmBtn: {
    flex: 1,
    padding: '10px',
    borderRadius: '10px',
    border: 'none',
    backgroundColor: '#DC2626',
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: '13px',
    cursor: 'pointer'
  }
};

export default AiSupportChatModal;
